import { useState, useEffect } from "react";
import { supabase, type Profile } from "@/lib/supabase";

export default function UsersManager() {
  const [userList, setUserList] = useState<Profile[]>([]);
  const [search, setSearch] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [showPassword, setShowPassword] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    role: "editor" as "super_admin" | "admin" | "editor",
    status: "active" as "active" | "inactive",
  });

  useEffect(() => { fetchUsers(); }, []);

  const fetchUsers = async () => {
    setIsLoading(true);
    const { data } = await supabase.from("profiles").select("*").order("created_at", { ascending: false });
    setUserList(data || []);
    setIsLoading(false);
  };

  const filtered = userList.filter(
    (u) =>
      u.name?.toLowerCase().includes(search.toLowerCase()) ||
      u.email?.toLowerCase().includes(search.toLowerCase())
  );

  const handleCreate = async () => {
    setCreating(true);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData.session?.access_token;
      if (!token) throw new Error("Not authenticated");

      const response = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/user-management`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            action: "create",
            name: formData.name,
            email: formData.email.toLowerCase().trim(),
            role: formData.role,
            status: formData.status,
          }),
        }
      );

      const result = await response.json();
      if (result.error) {
        alert("Error: " + result.error);
      } else {
        setShowPassword(result.tempPassword);
        await fetchUsers();
        // Keep modal open to show password, then close
        setTimeout(() => {
          setShowModal(false);
          setShowPassword(null);
          setFormData({ name: "", email: "", role: "editor", status: "active" });
        }, 4000);
      }
    } catch (err) {
      alert("Failed to create user: " + (err instanceof Error ? err.message : "Unknown error"));
    }
    setCreating(false);
  };

  const handleToggleStatus = async (user: Profile) => {
    const newStatus = user.status === "active" ? "inactive" : "active";
    const { error } = await supabase.from("profiles").update({ status: newStatus }).eq("id", user.id);
    if (!error) await fetchUsers();
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Are you sure you want to delete this user? This cannot be undone.")) return;
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData.session?.access_token;
      if (!token) throw new Error("Not authenticated");

      const response = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/user-management`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ action: "delete", userId: id }),
        }
      );
      const result = await response.json();
      if (result.error) {
        alert("Error: " + result.error);
      } else {
        await fetchUsers();
      }
    } catch (err) {
      alert("Failed to delete user: " + (err instanceof Error ? err.message : "Unknown error"));
    }
  };

  const handleResetPassword = async (email: string) => {
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData.session?.access_token;
      if (!token) throw new Error("Not authenticated");

      const response = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/user-management`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ action: "reset-password", email }),
        }
      );
      const result = await response.json();
      if (result.error) {
        alert("Error: " + result.error);
      } else {
        alert("Password reset email sent to " + email);
      }
    } catch (err) {
      alert("Failed to reset password: " + (err instanceof Error ? err.message : "Unknown error"));
    }
  };

  if (isLoading) {
    return (
      <div className="p-4 md:p-6 lg:p-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-48" />
          <div className="h-10 bg-gray-100 rounded w-full max-w-md" />
          <div className="h-64 bg-gray-100 rounded-xl" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 lg:p-8">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <h1 className="font-heading font-bold text-2xl text-primary-900">User Management</h1>
        <button onClick={() => { setFormData({ name: "", email: "", role: "editor", status: "active" }); setShowPassword(null); setShowModal(true); }} className="inline-flex items-center gap-2 px-4 py-2.5 bg-gold-600 hover:bg-gold-700 text-white text-sm font-medium rounded-lg transition-colors whitespace-nowrap">
          <i className="ri-add-line" /> Create User
        </button>
      </div>

      <div className="mb-4">
        <div className="relative max-w-md">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
          <input type="text" placeholder="Search users..." value={search} onChange={(e) => setSearch(e.target.value)} className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-gold-500 focus:ring-1 focus:ring-gold-500" />
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>
                <th className="px-4 md:px-6 py-3 text-xs font-semibold text-gray-500 uppercase">User</th>
                <th className="px-4 md:px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Role</th>
                <th className="px-4 md:px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Status</th>
                <th className="px-4 md:px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Last Login</th>
                <th className="px-4 md:px-6 py-3 text-xs font-semibold text-gray-500 uppercase text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.map((user) => (
                <tr key={user.id} className="hover:bg-gray-50/50">
                  <td className="px-4 md:px-6 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 flex items-center justify-center rounded-full bg-primary-50 text-primary-600 flex-shrink-0">
                        <i className="ri-user-line text-sm" />
                      </div>
                      <div>
                        <p className="font-medium text-sm text-gray-800">{user.name || "Unnamed User"}</p>
                        <p className="text-xs text-gray-400">{user.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 md:px-6 py-3">
                    <span className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-medium ${user.role === "super_admin" ? "bg-red-50 text-red-700" : user.role === "admin" ? "bg-primary-50 text-primary-700" : "bg-gold-50 text-gold-700"}`}>
                      {user.role.replace("_", " ")}
                    </span>
                  </td>
                  <td className="px-4 md:px-6 py-3">
                    <button
                      onClick={() => handleToggleStatus(user)}
                      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${user.status === "active" ? "bg-emerald-50 text-emerald-700" : "bg-gray-100 text-gray-500"}`}
                    >
                      <span className={`w-1.5 h-1.5 rounded-full ${user.status === "active" ? "bg-emerald-500" : "bg-gray-400"}`} />
                      {user.status}
                    </button>
                  </td>
                  <td className="px-4 md:px-6 py-3 text-sm text-gray-500 whitespace-nowrap">
                    {user.last_login ? new Date(user.last_login).toLocaleDateString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "Never"}
                  </td>
                  <td className="px-4 md:px-6 py-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => handleResetPassword(user.email)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-500 hover:text-primary-600 transition-colors" title="Reset Password" aria-label="Reset password"><i className="ri-key-line text-sm" /></button>
                      <button onClick={() => handleDelete(user.id)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-red-50 text-gray-500 hover:text-red-500 transition-colors" aria-label="Delete"><i className="ri-delete-bin-line text-sm" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {filtered.length === 0 && <div className="py-12 text-center text-gray-400 text-sm">No users found.</div>}
      </div>

      {/* Create User Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h2 className="font-heading font-bold text-lg text-primary-900">Create New User</h2>
              <button onClick={() => { setShowModal(false); setShowPassword(null); }} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-500"><i className="ri-close-line" /></button>
            </div>
            <div className="p-6 space-y-4">
              {showPassword ? (
                <div className="bg-emerald-50 border border-emerald-100 rounded-lg p-4">
                  <p className="text-sm text-emerald-700 font-medium mb-2">User created successfully!</p>
                  <p className="text-xs text-gray-600 mb-1">Temporary password:</p>
                  <div className="bg-white rounded border border-emerald-200 p-2 flex items-center justify-between">
                    <code className="text-sm font-mono text-gray-800">{showPassword}</code>
                    <button onClick={() => navigator.clipboard.writeText(showPassword)} className="text-emerald-600 text-xs hover:text-emerald-700"><i className="ri-file-copy-line" /> Copy</button>
                  </div>
                  <p className="text-xs text-gray-400 mt-2">Share this password with the user. Closing in a few seconds...</p>
                </div>
              ) : (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Full Name</label>
                    <input type="text" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-gold-500 focus:ring-1 focus:ring-gold-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Email Address</label>
                    <input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-gold-500 focus:ring-1 focus:ring-gold-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Role</label>
                    <select value={formData.role} onChange={(e) => setFormData({ ...formData, role: e.target.value as "super_admin" | "admin" | "editor" })} className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-gold-500 focus:ring-1 focus:ring-gold-500">
                      <option value="editor">Editor</option>
                      <option value="admin">Admin</option>
                      <option value="super_admin">Super Admin</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Status</label>
                    <select value={formData.status} onChange={(e) => setFormData({ ...formData, status: e.target.value as "active" | "inactive" })} className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-gold-500 focus:ring-1 focus:ring-gold-500">
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </select>
                  </div>
                  <p className="text-xs text-gray-400">A temporary password will be generated. You must share it with the user.</p>
                </>
              )}
            </div>
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100">
              <button onClick={() => { setShowModal(false); setShowPassword(null); }} className="px-4 py-2.5 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">Cancel</button>
              {!showPassword && (
                <button onClick={handleCreate} disabled={creating || !formData.email.trim()} className="px-5 py-2.5 bg-gold-600 hover:bg-gold-700 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-60">
                  {creating ? "Creating..." : "Create User"}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}